import {
  baseUrl,
  getCredentials,
  fetchOptions,
  fetchPostOptions,
  createQueryParameters,
} from './http';
import { FourOhOneError } from '../actionCreators/system';
import CustomToast from '../components/Redesing/custom-toast';
import { DEFAULT_ROWS_PER_PAGE } from '../constants';
import { downloadBlob } from '../utils';

export const getAllProblems = (params) => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const system_id = params?.systemId || systemId;
  const fetchOps = fetchOptions(adminEmail, adminToken, system_id);
  const queryParametersObject = {
    limit: params?.limit || DEFAULT_ROWS_PER_PAGE,
    ...(params?.before && { before: params?.before }),
    ...(params?.after && { after: params?.after }),
    ...(params?.cursor && { cursor: params?.cursor }),
    ...(params?.search && { search: params?.search }),
    ...(params?.status && { state: params?.status }),
    ...(params?.tags && { tags: params?.tags }),
    ...(params?.order && { order: params?.order }),
    ...(params?.ordinance && { ordinance: params?.ordinance }),
  };

  const queryParameters = createQueryParameters(queryParametersObject);

  return fetch(`${baseUrl}/maintenance?${queryParameters}`, fetchOps)
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 200) {
        return response;
      }
      FourOhOneError(response);
      throw new Error(response?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const exportProblems = (params) => {
  const { adminEmail, adminToken, systemId } = getCredentials();

  const fetchOps = fetchOptions(
    adminEmail,
    adminToken,
    Boolean(params?.organizationId) ? '' : systemId
  );

  const queryParametersObject = {
    ...(params?.search && { search: params?.search }),
    ...(params?.status && { state: params?.status }),
    ...(params?.tags && { tags: params?.tags }),
    ...(params?.order && { order: params?.order }),
    ...(params?.ordinance && { ordinance: params?.ordinance }),
    ...(params?.organizationId && { organization_id: params?.organizationId }),
    ...(params?.systemId && { system_id: params?.systemId }),
  };

  const queryParameters = createQueryParameters(queryParametersObject);

  return fetch(`${baseUrl}/maintenance/reports/csv?${queryParameters}`, fetchOps)
    .then((response) => {
      if (!response.ok) {
        throw Error(response.statusText || response.status);
      }
      return response;
    })
    .then((response) => response.blob())
    .then((blob) => downloadBlob(blob, 'support-tickets.csv'))
    .catch((error) => {
      CustomToast({
        type: 'error',
        message: `Oops! An error has occurred while exporting support tickets. Please try again later.`,
        duration: 5000,
      });
    });
};

export const getOneProblem = (problemId, organization_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchOptions(
    adminEmail,
    adminToken,
    Boolean(organization_id) ? '' : systemId
  );

  const url = new URL(`${baseUrl}/maintenance/${problemId}`);
  if (organization_id) {
    url.searchParams.set('organization_id', organization_id);
  }

  return fetch(url, fetchOps)
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 200) {
        return response.message;
      }
      FourOhOneError(response);
      throw new Error(response?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const getOneTicket = (ticketId, organization_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();

  const fetchOps = fetchOptions(
    adminEmail,
    adminToken,
    Boolean(organization_id) ? organization_id : systemId
  );

  const url = new URL(`${baseUrl}/maintenance/tickets/${ticketId}`);
  if (organization_id) {
    url.searchParams.set('organization_id', organization_id);
  }

  return fetch(url, fetchOps)
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 200) {
        return response.message;
      }
      FourOhOneError(response);
      throw new Error(response?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const getAllTickets = (organization_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchOptions(
    adminEmail,
    adminToken,
    Boolean(organization_id) ? '' : systemId
  );

  const url = new URL(`${baseUrl}/maintenance/tickets`);
  if (organization_id) {
    url.searchParams.set('organization_id', organization_id);
  }

  return fetch(url, fetchOps)
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 200) {
        return response.message;
      }
      FourOhOneError(response);
      throw new Error(response?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const getAllCompletedTasks = async (params) => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchOptions(
    adminEmail,
    adminToken,
    Boolean(params?.organizationId) ? '' : systemId
  );

  const queryParametersObject = {
    limit: params?.limit || DEFAULT_ROWS_PER_PAGE,
    ...(params?.before && { before: params?.before }),
    ...(params?.after && { after: params?.after?.toString() }),
    ...(params?.cursor && { cursor: params?.cursor }),
    ...(params?.location_id && { location_id: params?.location_id }),
    ...(params?.search && { search: params?.search }),
    ...(Boolean(params?.organizationId) && {
      organization_id: params?.organizationId,
      ...(params?.systemId && { system_id: params?.systemId }),
    }),
  };
  const queryParameters = createQueryParameters(queryParametersObject);

  const url = `${baseUrl}/maintenance/tasks/completed?${queryParameters}`;

  try {
    const response = await fetch(url, fetchOps);
    const data = await response.json();

    if (response.status === 200) {
      return data;
    }
    FourOhOneError(data);
    throw new Error(data?.message);
  } catch (error) {
    CustomToast({
      type: 'error',
      message: `Oops! An error has occurred while loading completed tasks. Please try again later. ${error}`,
      duration: 5000,
    });
  }
};

export const getOneCompletedTask = (taskId, organization_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchOptions(
    adminEmail,
    adminToken,
    Boolean(organization_id) ? '' : systemId
  );

  const url = new URL(`${baseUrl}/maintenance/tasks/completed/${taskId}`);
  if (organization_id) {
    url.searchParams.set('organization_id', organization_id);
  }

  return fetch(url, fetchOps)
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 200) {
        return response.message;
      }
      FourOhOneError(response);
      throw new Error(response?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const cancelTicket = (ticket, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    ticket,
    'PUT'
  );
  return fetch(`${baseUrl}/maintenance/tickets/cancel`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.status === 200) {
        return results;
      } else {
        FourOhOneError(results);
        throw new Error(results?.message);
      }
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const addNoteToTicket = (note, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    note,
    'PUT'
  );
  return fetch(`${baseUrl}/maintenance/tickets/note`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.status === 200) {
        return results;
      }
      FourOhOneError(results);
      throw new Error(results?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const editTicket = (ticket, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    ticket,
    'PUT'
  );
  return fetch(`${baseUrl}/maintenance/tickets/update`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.status === 200) {
        return results;
      }
      FourOhOneError(results);
      throw new Error(results?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const getAllTasks = (system_id = '', organization_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchOptions(adminEmail, adminToken, system_id || systemId);

  const url = new URL(`${baseUrl}/maintenance/tasks`);
  if (organization_id) {
    url.searchParams.set('organization_id', organization_id);
  }

  return fetch(url, fetchOps)
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 200) {
        return response.message;
      }
      FourOhOneError(response);
      throw new Error(response?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const createTicket = (ticket, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    ticket,
    'POST'
  );
  return fetch(`${baseUrl}/maintenance/tickets/add`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.status === 200) {
        return results;
      }
      FourOhOneError(results);
      throw new Error(results?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const closeTicket = (task, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    task,
    'PUT'
  );
  return fetch(`${baseUrl}/maintenance/tickets/close`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.status === 200) {
        return results;
      }
      FourOhOneError(results);
      throw new Error(results?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const completeTask = (task, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    task,
    'POST'
  );
  return fetch(`${baseUrl}/maintenance/tasks/close`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.status === 200) {
        return results;
      }
      FourOhOneError(results);
      throw new Error(results?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const updateIssue = (issue, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    issue,
    'PUT'
  );
  return fetch(`${baseUrl}/maintenance/update/${issue.id}`, fetchOps)
    .then((response) => {
      if (response.status === 200) {
        return response;
      }
      FourOhOneError(response);
      throw new Error(response?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const sendReplyEmail = (emailContent, issueId, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    emailContent,
    'PUT'
  );
  return fetch(`${baseUrl}/maintenance/reply/${issueId}`, fetchOps)
    .then((response) => {
      if (response.status === 200) {
        return response;
      }

      FourOhOneError(response);
      throw new Error(response?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const addNoteToReport = (note, system_id) => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    note,
    'PUT'
  );
  return fetch(`${baseUrl}/maintenance/report/note`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.status === 200) {
        return results;
      }
      FourOhOneError(results);
      throw new Error(results?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const getTags = (system_id = '', organization_id = '') => {
  const { adminEmail, adminToken, systemId, systemGid } = getCredentials();
  const fetchOps = fetchOptions(
    adminEmail,
    adminToken,
    Boolean(organization_id) ? '' : systemId
  );

  const queryParametersObject = {
    ...(Boolean(organization_id)
      ? { organization_id: organization_id, system_id: system_id }
      : { system_id: systemGid }),
  };
  const queryParameters = createQueryParameters(queryParametersObject);
  return fetch(`${baseUrl}/maintenance/support/tags?${queryParameters}`, fetchOps)
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 200) {
        return response.message;
      }
      FourOhOneError(response);
      throw new Error(response?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};
export const addTag = (tag, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    tag,
    'POST'
  );
  return fetch(`${baseUrl}/maintenance/support/tags`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.status === 200) {
        return results;
      }
      FourOhOneError(results);
      throw new Error(results?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const archiveTag = (tagId, payload, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    payload,
    'PUT'
  );
  return fetch(`${baseUrl}/maintenance/support/tags/${tagId}`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.status === 200) {
        return results;
      }
      FourOhOneError(results);
      throw new Error(results?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const addTagsToErrorReport = (reportGid, payload, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    payload,
    'PUT'
  );
  return fetch(`${baseUrl}/maintenance/support/update/tags/${reportGid}`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.status === 200) {
        return results;
      }
      FourOhOneError(results);
      throw new Error(results?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const getTicketImageUploadUrl = (
  ticketId,
  category = 'attachment',
  system_id = ''
) => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    { category },
    'POST'
  );
  return fetch(`${baseUrl}/maintenance/tickets/${ticketId}/image/upload-url`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.data) {
        return results.data;
      }
      FourOhOneError(results);
      throw new Error(results?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const confirmTicketImageUpload = (
  ticketId,
  imageId,
  note = '',
  system_id = ''
) => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  console.log('confirmTicketImageUpload', { ticketId, imageId, note, system_id });
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    { image_id: imageId, note },
    'POST'
  );
  return fetch(`${baseUrl}/maintenance/tickets/${ticketId}/image`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.status === 200) {
        return results.data;
      }
      console.log('results', results);
      FourOhOneError(results);
      throw new Error(results?.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const uploadImageToSignedUrl = async (signedUrl, file) => {
  try {
    const contentType = 'image/png';

    // Create a new Blob with the correct content-type
    const blob = new Blob([file], { type: contentType });

    const response = await fetch(signedUrl, {
      method: 'PUT',
      body: blob,
      headers: {
        'content-type': contentType,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload failed:', response.status, errorText);
      throw new Error(`Upload failed: ${response.status}`);
    }

    return true;
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
};
export const getSupportTicketsData = async (params) => {
  const { adminEmail, adminToken } = getCredentials();
  const fetchOps = fetchOptions(adminEmail, adminToken);

  const queryParametersObject = {
    limit: params?.limit || DEFAULT_ROWS_PER_PAGE,
    ...(params?.before && { before: params?.before }),
    ...(params?.after && { after: params?.after }),
    ...(params?.cursor && { cursor: params?.cursor }),
    ...(Boolean(params?.organizationId) && { organization_id: params.organizationId }),
    ...(Boolean(params?.system_id) && { system_id: params.system_id }),
    ...(Boolean(params?.status) && { state: params.status }),
    ...(Boolean(params?.tags) && { tags: params.tags }),
    ...(Boolean(params?.search) && { search: params.search }),
    ...(Boolean(params?.order) && { order: params.order }),
    ...(Boolean(params?.ordinance) && { ordinance: params.ordinance }),
  };

  const queryParameters = createQueryParameters(queryParametersObject);

  return fetch(`${baseUrl}/maintenance?${queryParameters}`, fetchOps)
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 200) {
        return response;
      }
      FourOhOneError(response);
      throw new Error(response?.message);
    })
    .catch((error) =>
      CustomToast({
        type: 'error',
        message: `Oops! An error has occurred while loading support tickets. Please try again later. ${error}`,
        duration: 5000,
      })
    );
};
